/**
 * The object exchange file (`.ptobj`): one library object — its name,
 * category, description, tags and full spec, plus its 3D model (GLB, base64)
 * when it has one — so it can move between organisations and back.
 *
 * JSON inside, versioned. Parsing never trusts the file: the spec goes
 * through the same validator as the editor and the server.
 */

import { isFixedClassCategory, parseObjectDefinitionSpec, requiredCategoryOf, type ObjectDefinitionIssue, type ObjectDefinitionSpec } from 'shared-types';

export const OBJECT_FILE_EXTENSION = '.ptobj';
/** What the file says it is (`format`), and the one version this app writes and reads. */
export const OBJECT_FILE_FORMAT = 'pointorama-object';
export const OBJECT_FILE_VERSION = 1;
/** Largest file accepted on import (an embedded model included). */
export const MAX_OBJECT_FILE_BYTES = 60 * 1024 * 1024;

export interface ObjectFileModel {
  /** Original name of the GLB (shown in the editor's assets). */
  fileName: string;
  /** The GLB, base64. */
  data: string;
}

export interface ObjectFile {
  format: typeof OBJECT_FILE_FORMAT;
  version: typeof OBJECT_FILE_VERSION;
  exportedAt: string;
  name: string;
  description: string | null;
  /** The category it was in (a built-in id, or the exporting organisation's own). */
  categoryId: string;
  /** That category's label, to find a matching one of the importing organisation. */
  categoryLabel: string | null;
  tags: string[];
  /** A smart object (AI-designed) stays one on import; files from before the flag are normal objects. */
  smart: boolean;
  spec: ObjectDefinitionSpec;
  model: ObjectFileModel | null;
}

export type ObjectFileParseError = 'NOT_AN_OBJECT_FILE' | 'NEWER_VERSION' | 'INVALID_SPEC';

export type ObjectFileParseResult = { ok: true; file: ObjectFile } | { ok: false; error: ObjectFileParseError; issues?: ObjectDefinitionIssue[] };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown, max: number): string | null => (typeof value === 'string' && value.trim() !== '' ? value.trim().slice(0, max) : null);

export function buildObjectFile(input: Omit<ObjectFile, 'format' | 'version' | 'exportedAt'>, now: Date = new Date()): ObjectFile {
  return { format: OBJECT_FILE_FORMAT, version: OBJECT_FILE_VERSION, exportedAt: now.toISOString(), ...input };
}

/** Read a `.ptobj` file's text: the object, or why it can't be imported. */
export function parseObjectFile(source: string): ObjectFileParseResult {
  let raw: unknown;
  try {
    raw = JSON.parse(source);
  } catch {
    return { ok: false, error: 'NOT_AN_OBJECT_FILE' };
  }
  if (!isRecord(raw) || raw['format'] !== OBJECT_FILE_FORMAT || typeof raw['version'] !== 'number') return { ok: false, error: 'NOT_AN_OBJECT_FILE' };
  if (raw['version'] > OBJECT_FILE_VERSION) return { ok: false, error: 'NEWER_VERSION' };
  const parsed = parseObjectDefinitionSpec(raw['spec']);
  if (!parsed.ok) return { ok: false, error: 'INVALID_SPEC', issues: 'issues' in parsed ? parsed.issues : [] };
  const modelRaw = raw['model'];
  const model = isRecord(modelRaw) && typeof modelRaw['data'] === 'string' && modelRaw['data'] !== '' ? { fileName: text(modelRaw['fileName'], 120) ?? 'model.glb', data: modelRaw['data'] } : null;
  return {
    ok: true,
    file: {
      format: OBJECT_FILE_FORMAT,
      version: OBJECT_FILE_VERSION,
      exportedAt: text(raw['exportedAt'], 40) ?? '',
      name: text(raw['name'], 80) ?? 'Imported object',
      description: text(raw['description'], 500),
      categoryId: text(raw['categoryId'], 64) ?? '',
      categoryLabel: text(raw['categoryLabel'], 60),
      tags: Array.isArray(raw['tags']) ? raw['tags'].flatMap((t) => (typeof t === 'string' && t.trim() !== '' ? [t.trim().slice(0, 32)] : [])).slice(0, 20) : [],
      smart: raw['smart'] === true,
      spec: parsed.spec,
      // A spec without a model never carries one along.
      model: parsed.spec.model3d ? model : null,
    },
  };
}

/**
 * The importing organisation's category for a file: doors / windows / dimensions their
 * fixed one; else the same built-in or own category, else one with the same
 * label, else `fallback`.
 */
export function importCategory(file: Pick<ObjectFile, 'spec' | 'categoryId' | 'categoryLabel'>, available: ReadonlyArray<{ id: string; label: string }>, fallback: string): string {
  const fixed = requiredCategoryOf(file.spec);
  if (fixed) return fixed;
  const usable = available.filter((c) => !isFixedClassCategory(c.id));
  if (usable.some((c) => c.id === file.categoryId)) return file.categoryId;
  const label = file.categoryLabel?.toLowerCase();
  return (label && usable.find((c) => c.label.toLowerCase() === label)?.id) || fallback;
}

/** A file name for an object (letters, digits, dashes). */
export function objectFileName(name: string): string {
  const base = name
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60);
  return `${base || 'object'}${OBJECT_FILE_EXTENSION}`;
}

/** Base64 of binary data (chunked: large models overflow a single String.fromCharCode). */
export function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export function fromBase64(data: string): Uint8Array<ArrayBuffer> {
  const binary = atob(data);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
